import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BarChart3 } from "lucide-react";

const OSF_URL = "https://osf.io/4g3a9/overview?view_only=5fc6436bfaf64eb6aad79385d740961a";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About us" },
  { to: "/toolkit", label: "Toolkit" },
  { to: "/contact", label: "Contact us" },
];

export const Footer = () => {
  const year = new Date().getFullYear();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/stats`)
      .then((r) => r.json())
      .then((d) => setStats(d))
      .catch(() => {});
  }, []);

  return (
    <footer className="woven-texture text-[#FAF8F5]" data-testid="site-footer">
      <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
        <div className="grid gap-10 md:grid-cols-3">
          {/* Contact address — exact wording */}
          <div>
            <h2 className="font-serif text-lg font-semibold text-[#FAF8F5]">Contact</h2>
            <address className="mt-4 not-italic text-[15px] leading-relaxed text-[#FAF8F5]/80" data-testid="footer-address">
              Dr. Sumanth Kumbargere Nagraj<br />
              Peninsula Dental School<br />
              University of Plymouth<br />
              Drake Circus, PL4 8AA<br />
              Plymouth, United Kingdom
            </address>
            <Link
              to="/data-protection"
              className="mt-4 block text-[13px] text-[#FAF8F5]/70 underline underline-offset-2 hover:text-melt-amber"
              data-testid="footer-policy-link"
            >
              Data Protection and Cookie Policy
            </Link>
          </div>

          {/* Page links */}
          <nav aria-label="Footer" className="md:justify-self-center">
            <h2 className="font-serif text-lg font-semibold text-[#FAF8F5]">Pages</h2>
            <ul className="mt-4 space-y-2 text-[15px]">
              {PAGES.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    className="text-[#FAF8F5]/80 transition-colors hover:text-melt-amber"
                    data-testid={`footer-link-${p.label.split(" ")[0].toLowerCase()}`}
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Repository */}
          <div>
            <h2 className="font-serif text-lg font-semibold text-[#FAF8F5]">Repository</h2>
            <p className="mt-4 text-[15px] text-[#FAF8F5]/80">
              MELT materials are archived openly for reuse and citation.
            </p>
            <a
              href={OSF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-[15px] font-medium text-melt-amber hover:underline"
              data-testid="footer-osf-link"
            >
              Open Science Framework
              <ExternalLink size={15} aria-hidden="true" />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-[13px] text-[#FAF8F5]/60 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">
            © {year} MELT — the MultiEthno-Linguistic Toolkit. Freely available for non-commercial research use.
          </p>
          {stats && (
            <p className="inline-flex items-center gap-2 text-[#FAF8F5]/70" data-testid="footer-stats" aria-label="Aggregate site statistics">
              <BarChart3 size={14} aria-hidden="true" className="text-melt-amber" />
              <span>{stats.page_views.toLocaleString()} page views</span>
              <span aria-hidden="true" className="text-[#FAF8F5]/30">·</span>
              <span>{stats.downloads.toLocaleString()} document downloads</span>
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
